/* Layers — grouped, individually toggleable.
 * Sensor and weapon envelopes are the highest-value layers in the product: they answer
 * "can I be seen?" and "can I shoot?", the two questions that drive every decision.
 */
import { Eye, EyeOff, X } from 'lucide-react'
import { useDeck, type LayerId } from '../state/store'
import { BasemapPanel } from './BasemapPanel'

interface Group {
  title: string
  items: { id: LayerId; label: string; note?: string }[]
}

const GROUPS: Group[] = [
  {
    title: 'Situation',
    items: [
      { id: 'units', label: 'Own units' },
      { id: 'contacts', label: 'Contacts', note: 'estimates, not truth' },
      { id: 'tracks', label: 'Track history' },
    ],
  },
  {
    title: 'Envelopes',
    items: [
      { id: 'sensorRanges', label: 'Sensor coverage', note: 'geometric' },
      { id: 'weaponEnvelopes', label: 'Weapon range', note: 'geometric' },
      { id: 'threatEnvelopes', label: 'Assessed threat', note: 'hostile' },
    ],
  },
  {
    title: 'Planning',
    items: [
      { id: 'missions', label: 'Mission areas' },
      { id: 'refPoints', label: 'Reference points' },
      { id: 'zones', label: 'Exclusion zones' },
    ],
  },
  {
    title: 'Geospatial',
    items: [
      { id: 'terrain', label: 'Terrain / coastline' },
      { id: 'bathymetry', label: 'Bathymetry' },
      { id: 'graticule', label: 'Graticule' },
    ],
  },
  {
    title: 'Umpire',
    items: [{ id: 'truth', label: 'Truth overlay', note: 'role-gated' }],
  },
]

export function LayersPanel() {
  const open = useDeck((s) => s.layersOpen)
  const toggle = useDeck((s) => s.toggleLayersPanel)
  const layers = useDeck((s) => s.layers)
  const toggleLayer = useDeck((s) => s.toggleLayer)

  if (!open) return null

  return (
    <div
      className="panel absolute right-3 top-3 z-20 w-64 overflow-hidden"
      role="dialog"
      aria-label="Layers"
    >
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span className="title">Layers</span>
        <button
          className="ctl"
          style={{ width: 20, height: 20, padding: 0 }}
          onClick={toggle}
          aria-label="Close layers panel"
        >
          <X size={12} />
        </button>
      </div>

      <div className="max-h-[70vh] overflow-y-auto py-1">
        <BasemapPanel />
        <div className="mx-3 my-1 h-px" style={{ background: 'var(--color-line-2)' }} />
        {GROUPS.map((g) => (
          <div key={g.title} className="mb-1">
            <div className="label px-3 py-1">{g.title}</div>
            {g.items.map((item) => {
              const on = layers[item.id]
              const isTruth = item.id === 'truth'
              return (
                <button
                  key={item.id}
                  className="flex w-full items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)]"
                  onClick={() => toggleLayer(item.id)}
                  role="switch"
                  aria-checked={on}
                >
                  {on ? (
                    <Eye
                      size={12}
                      style={{ color: isTruth ? 'var(--color-truth)' : 'var(--color-accent-hi)' }}
                      aria-hidden
                    />
                  ) : (
                    <EyeOff size={12} style={{ color: 'var(--color-text-3)' }} aria-hidden />
                  )}
                  <span
                    className="min-w-0 flex-1 truncate"
                    style={{
                      fontSize: 11.5,
                      color: on ? 'var(--color-text-1)' : 'var(--color-text-3)',
                    }}
                  >
                    {item.label}
                  </span>
                  {item.note && (
                    <span className="label shrink-0" style={{ fontSize: 8 }}>
                      {item.note}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
